/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { createContext } from 'react';

export type CustomerPortalAccount = {
	userDetails: {
		firstName: string;
		lastName: string;
		email: string;
		id: string;
	};
	organizations: Array<Organization>;
	subscriptions: Array<Subscription>;
};

export type CustomerPortalCompletedFlowType = {
	flow?: 'signup' | 'signin';
};

export const enum CustomerPortalAction {
	LOG_IN = 'log-in',
	LOG_OUT = 'log-out',
	SIGN_UP = 'sign-up'
}

type SuccessCallback = { onSuccess?: null | ( () => void ) };

export interface CustomerPortalActionOptions {
	[ CustomerPortalAction.LOG_IN ]?: SuccessCallback;
	[ CustomerPortalAction.LOG_OUT ]?: SuccessCallback;
	[ CustomerPortalAction.SIGN_UP ]: SuccessCallback & { variant: 'sidebar' | 'steps' };
}

export type CustomerPortalActionArgs<T extends CustomerPortalAction> = undefined extends CustomerPortalActionOptions[ T ] ?
	[ action: T, options?: CustomerPortalActionOptions[ T ] ] :
	[ action: T, options: CustomerPortalActionOptions[ T ] ];

export type CustomerPortalIntegrationType = {
	performCustomerPortalAction: <T extends CustomerPortalAction>( ...args: CustomerPortalActionArgs<T> ) => void;
	emitCustomerPortalEvent: ( { type, stage }: emitCustomerPortalEventArgs ) => void;
	customerPortalAccount: null | CustomerPortalAccount;
	isLoggedIn: boolean;
	isTrialActive: boolean;
	plan?: CustomerPortalAccountPlan;
	hasSelfHostedAccess: boolean;
	hasCloudAccess: boolean;
	hasOldGPLAccess: boolean;
	featuresAvailableInPlan: Array<string>;
	getCustomerPortalUrl: () => string;
	getUpgradePlanUrl: ( organizationId: string, subscriptionId?: string ) => string;
};

export const CustomerPortalContext = createContext<CustomerPortalIntegrationType>( {
	performCustomerPortalAction: () => {},
	emitCustomerPortalEvent: () => {},
	customerPortalAccount: null,
	isLoggedIn: false,
	isTrialActive: false,
	plan: undefined,
	hasSelfHostedAccess: false,
	hasCloudAccess: false,
	hasOldGPLAccess: false,
	featuresAvailableInPlan: [],
	getCustomerPortalUrl: () => '',
	getUpgradePlanUrl: () => ''
} );

export type Organization = {
	hasOverduePayment: boolean;
	id: string;
	membersCount: number;
	name: string;
	permissions: Array<string>;
	salesAssistedSubscriptions: Array<string>;
	selfServiceSubscriptions: Array<string>;
	subscriptionsCount: number;
};

type SubscriptionService = {
	serviceType: string;
	name: string;
	billingPeriods: Array<{
		from: string;
		to: string;
	}>;
};

type SubscriptionProduct = {
	id: string;
	billingMethod: string;
	name: string;
	slug: string;
	kind: string;
	parentProductId: string | null;
	nextPaymentAt: string;
	currentPeriodStartedAt: string;
	currentPeriodEndsAt: string;
	quantity: number;
	price: number;
};

type SubscriptionBenefit = {
	label: string;
	children: Array<{
		id: string;
		unit: string | null;
		label: string;
		value: string;
		category: string;
		priority: number;
		isEnabled: boolean;
		unitLimit: number | null;
		trialValue: string;
		valueLimit: string;
		isTrialEntitled: boolean;
		parentBenefitId: string | null;
		isAddon: boolean;
		isTrial: boolean;
		limits: Array<unknown>;
	}>;
};

type SubscriptionEnvironment = {
	urls: {
		apiBase: string;
		developmentToken: string;
		easyImageUpload: null;
		websocket: string;
	};
};

type SubscriptionKey = {
	label: string;
	type: string;
	children: Array<SubscriptionKeyItem>;
};

type SubscriptionKeyItem = {
	label: string;
	type: string;
	version: string;
	versionRange: {
		from: string;
		to: string | null;
	};
	keys: Array<{
		type: 'production' | 'development' | 'trial';
		value: `${ string }.${ string }.${ string }`;
		expiresAt: string;
		subscription: SubscriptionName;
	}>;
};

type SubscriptionName = string;

export type CustomerPortalAccountPlan = 'free' | 'premium' | 'basic';

export type Subscription = {
	details: {
		trial: null | {
			until: string;
		};
		nextPlan: unknown;
		services: Array<SubscriptionService>;
		id: string;
		projectName: string;
		endsAt: string | null;
		cancelledAt: string | null;
		support: {
			supportToken: string;
			supportLimit: number | null;
		};
		products: Array<SubscriptionProduct>;
		plan?: CustomerPortalAccountPlan;
		hasCloudEnvironments: boolean;
		hasLicenseKeys: boolean;
		hasUsageStatistics: boolean;
		name: SubscriptionName;
		billing: {
			period: string;
			paymentStatus: string;
			paidUntil: string;
		};
		selfService: {
			subscriptionProductId: string;
			editorLoadsProductId: string;
			addonProductIds: Array<string>;
			converterProductIds: Array<string>;
		};
		benefits: Array<SubscriptionBenefit>;
	};
	environments: Array<SubscriptionEnvironment>;
	keys: Array<SubscriptionKey>;
	organizationId: string;
	type: string;
};

export type BuilderSetupStage = 'Builder Setup Started' | 'Builder Setup Incomplete' | 'Builder Setup Complete';

export type emitCustomerPortalEventArgs = {
	type: 'sign-out';
	stage?: never;
} | {
	type: 'stage';
	stage: BuilderSetupStage;
};

type CustomerPortalMessageSignOut = {
	isBuilderEvent: true;
	type: 'sign-out';
};

type CustomerPortalMessageStage = {
	isBuilderEvent: true;
	type: 'stage';
	payload: {
		stage: BuilderSetupStage;
	};
};

export type CustomerPortalMessage = CustomerPortalMessageSignOut | CustomerPortalMessageStage;
